import { Info } from 'lucide-react';

import { useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Badge } from '@splitfiesta/ui';

export interface PlaceholderBadgeProps {
  className?: string;
}

/**
 * "Not joined yet" chip for a placeholder member row, with an info button
 * that explains what that means on hover (title) and on tap (touch has no
 * hover, so the same text opens as a small popover).
 */
export function PlaceholderBadge({ className = '' }: PlaceholderBadgeProps) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Portalled, fixed-position and measured on open: the badge sits inside
  // cards and lists that clip overflow (and some ancestors are transformed,
  // which would re-anchor a fixed child), often at a row's right edge, so an
  // in-flow popover gets cut off.
  const [popover, setPopover] = useState<{ top: number; left: number } | null>(
    null
  );
  const toggle = () => {
    if (popover) {
      setPopover(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 256;
    const margin = 16;
    setPopover({
      top: rect.bottom + 4,
      left: Math.max(
        margin,
        Math.min(rect.left, window.innerWidth - width - margin)
      ),
    });
  };

  useNamespace('groups');

  const explainer = t('groups:placeholderMembers.badgeExplainer');

  // `Badge` drops unknown props, so the testid lives on the wrapper.
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 w-fit ${className}`}
      data-testid="placeholder-badge"
    >
      <Badge variant="neutral" size="sm">
        {t('groups:placeholderMembers.badge')}
      </Badge>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        onBlur={() => setPopover(null)}
        title={explainer}
        aria-label={t('groups:placeholderMembers.badge')}
        aria-expanded={popover !== null}
        data-testid="placeholder-badge-info"
        className="rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:text-gray-500 dark:hover:text-gray-300"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {popover &&
        createPortal(
          <span
            role="tooltip"
            data-testid="placeholder-badge-explainer"
            style={{ top: popover.top, left: popover.left }}
            className="fixed z-50 w-64 rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-700 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            {explainer}
          </span>,
          document.body
        )}
    </span>
  );
}

export default PlaceholderBadge;
